import { useState } from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import { formatNumber } from "./utils";

const useStyles = makeStyles({
  card: {
    backgroundColor: themeColorsDesingSystem.background.main,
    borderRadius: 4,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: `1px solid ${themeColorsDesingSystem.stone.main}`,
    padding: "1.25rem",
    height: 350,
    minHeight: 350,
    maxHeight: 350,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  title: {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: themeColorsDesingSystem.gray.dark,
    marginBottom: "1rem",
    paddingBottom: "0.5rem",
    borderBottom: `1px solid ${themeColorsDesingSystem.stone.main}`,
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0.5rem 0.5rem",
    cursor: "pointer",
    borderBottom: `1px solid ${themeColorsDesingSystem.stone.main}`,
    transition: "background-color 0.15s",
    "&:hover": {
      backgroundColor: themeColorsDesingSystem.stone.light,
    },
  },
  label: {
    fontSize: "0.8125rem",
    color: themeColorsDesingSystem.gray.dark,
    fontWeight: 500,
    paddingRight: "0.5rem",
  },
  value: {
    fontSize: "0.8125rem",
    fontWeight: 700,
    color: themeColorsDesingSystem.text.primary,
    flexShrink: 0,
  },
  scrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: "0.5rem",
  },
  detailSection: {
    marginTop: "0.75rem",
    "&:first-of-type": {
      marginTop: 0,
    },
  },
  detailSectionTitle: {
    fontSize: "0.6875rem",
    fontWeight: 700,
    color: themeColorsDesingSystem.gray.dark,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: "0.375rem",
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0.375rem 0",
    borderTop: `1px solid ${themeColorsDesingSystem.stone.light}`,
    "&:first-of-type": {
      borderTop: "none",
    },
  },
  detailText: {
    fontSize: "0.75rem",
    color: themeColorsDesingSystem.gray.main,
    flex: 1,
    paddingRight: "0.5rem",
  },
  detailValue: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: themeColorsDesingSystem.text.primary,
    flexShrink: 0,
  },
  emptyState: {
    fontSize: "0.8125rem",
    color: themeColorsDesingSystem.gray.main,
    padding: "1rem 0",
  },
});

function DetailSection({
  title,
  items,
  renderLabel,
  renderValue,
  emptyMessage,
}) {
  const classes = useStyles();

  if (!items?.length) {
    return (
      <Box className={classes.detailSection}>
        <Typography className={classes.detailSectionTitle}>{title}</Typography>
        <Typography className={classes.detailText}>{emptyMessage}</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.detailSection}>
      <Typography className={classes.detailSectionTitle}>{title}</Typography>
      {items.map((item, idx) => (
        <Box key={idx} className={classes.detailRow}>
          <Typography className={classes.detailText}>
            {renderLabel(item)}
          </Typography>
          <Typography className={classes.detailValue}>
            {renderValue(item)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function PeriodIndicatorsAccordion({ indicators = [] }) {
  const classes = useStyles();
  const [accordionAberto, setAccordionAberto] = useState(null);

  const toggleAccordion = (id) => {
    setAccordionAberto(accordionAberto === id ? null : id);
  };

  const hasDetails = (item) =>
    item.detalhes?.length > 0 ||
    item.detalhesPorPeriodo?.length > 0 ||
    item.detalhesPorTela?.length > 0;

  return (
    <Box className={classes.card}>
      <Typography className={classes.title}>Indicadores do Período</Typography>
      <Box className={classes.scrollArea}>
        {!indicators.length && (
          <Typography className={classes.emptyState}>
            Nenhum indicador encontrado para o período selecionado.
          </Typography>
        )}
        {indicators.map((item) => (
          <Box key={item.id}>
            <Box
              className={classes.row}
              onClick={() => toggleAccordion(item.id)}
            >
              <Typography className={classes.label}>{item.nome}</Typography>
              <Typography className={classes.value}>
                {formatNumber(item.valor) ?? "0"}
              </Typography>
            </Box>
            {accordionAberto === item.id && (
              <Box
                style={{
                  padding: "0.5rem 1rem 0.75rem",
                  backgroundColor: themeColorsDesingSystem.stone.light,
                  borderRadius: "0 0 4px 4px",
                }}
              >
                {!hasDetails(item) ? (
                  <Typography className={classes.detailText}>
                    Sem detalhamento disponível para este indicador.
                  </Typography>
                ) : (
                  <>
                    <DetailSection
                      title="Por perfil"
                      items={item.detalhes}
                      renderLabel={(det) => det.perfil}
                      renderValue={(det) => formatNumber(det.qtd)}
                      emptyMessage="Sem detalhamento por perfil."
                    />
                    <DetailSection
                      title="Por período"
                      items={item.detalhesPorPeriodo}
                      renderLabel={(det) => det.periodo}
                      renderValue={(det) => formatNumber(det.qtd)}
                      emptyMessage="Sem detalhamento por período."
                    />
                    <DetailSection
                      title="Por tela"
                      items={item.detalhesPorTela}
                      renderLabel={(det) => det.tela}
                      renderValue={(det) => formatNumber(det.qtd)}
                      emptyMessage="Sem telas associadas a este indicador."
                    />
                  </>
                )}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
